import { useEffect, useState } from "react";
import AdminNav from "../../components/AdminNav";
import {
  getAdminList, createAdminAccount, deleteAdminAccount,
  updateAdminPermissions, getSupportRoleDefaults, updateSupportRoleDefaults
} from "../../api/client";
import { useAuth } from "../../context/AuthContext";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

const PERMISSION_LABELS = {
  riders: "Fleet (riders)",
  orders: "Orders",
  revenue: "Revenue dashboard",
  users: "Users",
  agents: "Agents",
  fleetOwners: "Fleet Owners",
  invoices: "B2B Invoices"
};

function PermissionChecklist({ allPermissions, selected, onChange, disabled }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "8px" }}>
      {allPermissions.map((key) => (
        <label key={key} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 400 }}>
          <input
            type="checkbox"
            checked={selected.includes(key)}
            disabled={disabled}
            onChange={(e) => {
              onChange(e.target.checked ? [...selected, key] : selected.filter((p) => p !== key));
            }}
          />
          {PERMISSION_LABELS[key] || key}
        </label>
      ))}
    </div>
  );
}

export default function AdminAdmins() {
  const { account } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin", permissions: [] });
  const [formError, setFormError] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  // Support role defaults — the baseline permission set a newly created
  // support admin picks up unless the creator overrides it in `form.permissions`.
  const [allPermissions, setAllPermissions] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [defaultsBusy, setDefaultsBusy] = useState(false);
  const [defaultsSaved, setDefaultsSaved] = useState(false);

  // Per-admin permission editing (existing support accounts)
  const [editingId, setEditingId] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState([]);
  const [editBusy, setEditBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await getAdminList();
      setAdmins(data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not load admin accounts.");
    } finally {
      setLoading(false);
    }
  };

  const loadDefaults = async () => {
    try {
      const { data } = await getSupportRoleDefaults();
      setAllPermissions(data.allPermissions);
      setDefaults(data.permissions);
      setForm((f) => (f.role === "support" && f.permissions.length === 0 ? { ...f, permissions: data.permissions } : f));
    } catch (err) {
      setError(err.response?.data?.error || "Could not load support role defaults.");
    }
  };

  useEffect(() => { load(); loadDefaults(); }, []);

  const handleSaveDefaults = async () => {
    setDefaultsBusy(true);
    setDefaultsSaved(false);
    setError("");
    try {
      const { data } = await updateSupportRoleDefaults(defaults);
      setDefaults(data.permissions);
      setDefaultsSaved(true);
    } catch (err) {
      setError(err.response?.data?.error || "Could not save support role defaults.");
    } finally {
      setDefaultsBusy(false);
    }
  };

  const startEditingPermissions = (admin) => {
    setEditingId(admin._id);
    setEditingPermissions(admin.permissions || []);
  };

  const handleSavePermissions = async (adminId) => {
    setEditBusy(true);
    setError("");
    try {
      await updateAdminPermissions(adminId, editingPermissions);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not save permissions.");
    } finally {
      setEditBusy(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.name || !form.email || !form.password) {
      setFormError("All fields are required.");
      return;
    }
    setCreating(true);
    try {
      await createAdminAccount(form);
      setForm({ name: "", email: "", password: "", role: "admin", permissions: defaults });
      setShowCreate(false);
      await load();
    } catch (err) {
      setFormError(err.response?.data?.error || "Could not create admin account.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`Delete admin account for ${admin.name}? This cannot be undone.`)) return;
    setBusyId(admin._id);
    setError("");
    try {
      await deleteAdminAccount(admin._id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not delete admin account.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="admin-app">
      <AdminNav />
      <div className="page wide page-with-nav">

        <div className="brand-header">
          <span className="brand-logo">🔑</span>
          <span className="brand-name">Admin Accounts</span>
          <p className="brand-tagline">Create and manage administrator accounts</p>
        </div>

        {/* Header actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
          <button
            style={{ width: "auto", padding: "10px 20px", marginTop: 0 }}
            onClick={() => { setShowCreate(!showCreate); setFormError(""); }}
          >
            {showCreate ? "✕ Cancel" : "+ Create New Admin"}
          </button>
        </div>

        {/* Create Admin Form */}
        {showCreate && (
          <div className="delivery-card" style={{ marginBottom: "24px" }}>
            <h3 style={{ marginBottom: "16px" }}>➕ New Administrator Account</h3>
            {formError && <p className="error">{formError}</p>}
            <form onSubmit={handleCreate}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Full Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Jane Admin"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    style={{ margin: 0 }}
                    required
                  />
                </div>
                <div>
                  <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Email Address</label>
                  <input
                    type="email"
                    placeholder="admin@deliverng.com"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    style={{ margin: 0 }}
                    required
                  />
                </div>
              </div>
              <div style={{ marginTop: "12px" }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Temporary Password</label>
                <input
                  type="password"
                  placeholder="Minimum 8 characters..."
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  style={{ margin: 0, width: "100%" }}
                  required
                  minLength={8}
                />
              </div>
              <div style={{ marginTop: "12px" }}>
                <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Account Type</label>
                <select
                  value={form.role}
                  onChange={e => {
                    const role = e.target.value;
                    setForm(f => ({ ...f, role, permissions: role === "support" ? defaults : [] }));
                  }}
                  style={{ margin: 0 }}
                >
                  <option value="admin">Admin — full access</option>
                  <option value="support">Support — restricted access</option>
                </select>
                <p className="hint">Support can't set pricing, create/delete admin accounts, or view the pricing history log.</p>
              </div>
              {form.role === "support" && (
                <div style={{ marginTop: "8px" }}>
                  <label style={{ fontSize: "12px", color: "var(--text-muted)", display: "block" }}>Permissions for this account</label>
                  <PermissionChecklist
                    allPermissions={allPermissions}
                    selected={form.permissions}
                    onChange={(permissions) => setForm(f => ({ ...f, permissions }))}
                  />
                </div>
              )}
              <button type="submit" disabled={creating} style={{ marginTop: "16px" }}>
                {creating ? "Creating..." : "✅ Create Admin Account"}
              </button>
            </form>
          </div>
        )}

        {/* Support Role Defaults — the baseline permission set applied to a
            newly created support admin unless overridden above. */}
        <div className="delivery-card" style={{ marginBottom: "24px" }}>
          <h3 style={{ marginBottom: "4px" }}>🧩 Support Role Defaults</h3>
          <p className="hint">What a new Support account is granted by default. Doesn't change any existing support account's own permissions.</p>
          <PermissionChecklist
            allPermissions={allPermissions}
            selected={defaults}
            onChange={setDefaults}
          />
          <button
            className="secondary"
            style={{ width: "auto", padding: "8px 16px", marginTop: "12px" }}
            disabled={defaultsBusy}
            onClick={handleSaveDefaults}
          >
            {defaultsBusy ? "Saving..." : defaultsSaved ? "✓ Saved" : "Save Defaults"}
          </button>
        </div>

        {error && <p className="error">{error}</p>}

        {loading && (
          <div className="empty-state">
            <span className="empty-state-icon">📡</span>
            <p className="empty-state-text waiting-dots">Loading admin accounts</p>
          </div>
        )}

        {/* Admins List */}
        {!loading && admins.map(admin => {
          const isSelf = admin._id === account?._id;
          return (
            <div key={admin._id} className="delivery-card" style={{ marginBottom: "14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{
                      width: "40px", height: "40px", borderRadius: "50%",
                      background: "linear-gradient(135deg, var(--warning), var(--danger))",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 800, fontSize: "16px", color: "#fff"
                    }}>
                      {admin.name?.charAt(0)?.toUpperCase() || "A"}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700 }}>
                        {admin.name}
                        {isSelf && <span style={{ marginLeft: "8px", fontSize: "11px", background: "rgba(22, 199, 132,0.2)", color: "var(--success-hover)", padding: "2px 8px", borderRadius: "20px" }}>You</span>}
                      </div>
                      <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>{admin.email}</div>
                      <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                        {admin.role === "support" ? "Support" : "Administrator"} · Since {formatDate(admin.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  {admin.role === "support" && editingId !== admin._id && (
                    <button
                      className="secondary"
                      style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px" }}
                      onClick={() => startEditingPermissions(admin)}
                    >
                      ✏️ Edit Permissions
                    </button>
                  )}
                  {!isSelf && (
                    <button
                      className="secondary"
                      style={{ width: "auto", padding: "7px 14px", marginTop: 0, fontSize: "13px", borderColor: "var(--danger)", color: "var(--danger)" }}
                      disabled={busyId === admin._id}
                      onClick={() => handleDelete(admin)}
                    >
                      {busyId === admin._id ? "Deleting..." : "🗑️ Remove Admin"}
                    </button>
                  )}
                </div>
              </div>

              {admin.role === "support" && editingId !== admin._id && (
                <p className="hint" style={{ marginTop: "10px" }}>
                  {admin.permissions?.length
                    ? admin.permissions.map((p) => PERMISSION_LABELS[p] || p).join(", ")
                    : "No permissions granted yet."}
                </p>
              )}

              {editingId === admin._id && (
                <div style={{ marginTop: "12px", borderTop: "1px solid var(--border)", paddingTop: "12px" }}>
                  <PermissionChecklist
                    allPermissions={allPermissions}
                    selected={editingPermissions}
                    onChange={setEditingPermissions}
                  />
                  <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                    <button
                      style={{ width: "auto", padding: "7px 16px", marginTop: 0 }}
                      disabled={editBusy}
                      onClick={() => handleSavePermissions(admin._id)}
                    >
                      {editBusy ? "Saving..." : "Save"}
                    </button>
                    <button
                      className="secondary"
                      style={{ width: "auto", padding: "7px 16px", marginTop: 0 }}
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {!loading && admins.length === 0 && (
          <div className="empty-state">
            <span className="empty-state-icon">🔑</span>
            <p className="empty-state-text">No admin accounts found.</p>
          </div>
        )}
      </div>
    </div>
  );
}
